// calculateProductionPrice.js

export const toNumber = (value, fallback = 0) => {
    const prepared = String(value ?? '').replace(',', '.').trim()

    if (!prepared) return fallback

    const number = Number(prepared)

    if (!Number.isFinite(number)) return fallback

    return number
}

export const roundMoney = (value) => {
    return Math.round(toNumber(value, 0) * 100) / 100
}

export const roundByStep = (value, step = 1) => {
    const safeStep = Math.max(1, toNumber(step, 1))
    const safeValue = toNumber(value, 0)

    return Math.round(safeValue / safeStep) * safeStep
}

export const formatMoney = (value, emptyText = 'Цена не указана') => {
    const number = Number(value)

    if (!Number.isFinite(number) || number <= 0) {
        return emptyText
    }

    return `${Math.round(number).toLocaleString('ru-RU')} ₽`
}

const asObject = (value) => {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value
        : {}
}

const DEFAULT_PUBLIC_PRICING_POLICY = Object.freeze({
    blankWidthStepMm: 50,
    blankHeightStepMm: 50,
    blankThicknessStepMm: 10,
    blankMinimumThicknessMm: 30,
    millingThicknessStepMm: 10,
    millingThicknessMarkupPercent: 5,
    millingThicknessMarkupMaxPercent: 50,
    roundTo: 0.01,
})

let publicBlankPricingSnapshot = {
    materials: [],
    pricingPolicy: { ...DEFAULT_PUBLIC_PRICING_POLICY },
}

const normalizeMaterialKey = (value) => {
    const key = String(value || '').trim().toLowerCase()
    return key === 'kapustinsky' ? 'vinga' : key
}

export const setPublicBlankPricingSnapshot = ({ materials = [], pricingPolicy = {} } = {}) => {
    publicBlankPricingSnapshot = {
        materials: Array.isArray(materials) ? materials.filter(Boolean) : [],
        pricingPolicy: {
            ...DEFAULT_PUBLIC_PRICING_POLICY,
            ...asObject(pricingPolicy),
        },
    }

    return publicBlankPricingSnapshot
}

export const getPublicBlankPricingSnapshot = () => publicBlankPricingSnapshot

const getPublicBlankMaterial = (materialKey = '') => {
    const requested = normalizeMaterialKey(materialKey)
    if (!requested) return null

    return publicBlankPricingSnapshot.materials.find((material) => (
        normalizeMaterialKey(material?.key) === requested
    )) || null
}

const roundMoneyByStep = (value, step = 0.01) => {
    const safeStep = toNumber(step, 0.01) > 0 ? toNumber(step, 0.01) : 0.01
    return roundMoney(Math.round(toNumber(value, 0) / safeStep) * safeStep)
}

export const getArea = (dimensions = {}) => {
    const source = asObject(dimensions)
    const explicit = toNumber(source.area, 0)

    if (explicit > 0) return explicit

    const width = toNumber(source.width, 0)
    const height = toNumber(source.height, 0)

    return width > 0 && height > 0 ? width * height : 0
}

export const getVolume = (dimensions = {}) => {
    const source = asObject(dimensions)
    const explicit = toNumber(source.volume, 0)

    if (explicit > 0) return explicit

    const width = toNumber(source.width, 0)
    const height = toNumber(source.height, 0)
    const length = toNumber(source.length, 0)

    return width > 0 && height > 0 && length > 0 ? width * height * length : 0
}

export const getLinearMeters = (dimensions = {}) => {
    const source = asObject(dimensions)
    const explicit = toNumber(source.linearMeters, 0)

    if (explicit > 0) return explicit

    const length = toNumber(source.length, 0)

    if (length > 0) return length

    const width = toNumber(source.width, 0)

    if (width > 0) return width

    const height = toNumber(source.height, 0)

    if (height > 0) return height

    return 0
}

export const calculateByMode = ({
    calcMode,
    price,
    pricePerMeter,
    pricePerSquareMeter,
    pricePerCubicMeter,
    pricePerSymbol,
    pricePerUnit,
    minPrice,
    dimensions,
    quantity,
    symbolsCount,
}) => {
    const qty = Math.max(1, toNumber(quantity, 1))
    const safeMode = calcMode || 'fixed'

    let unitValue = 1
    let unitPrice = 0
    let subtotal = 0

    if (safeMode === 'linearMeter') {
        unitValue = getLinearMeters(dimensions)
        unitPrice = toNumber(pricePerMeter, 0)
        subtotal = unitValue * unitPrice * qty
    } else if (safeMode === 'squareMeter') {
        unitValue = getArea(dimensions)
        unitPrice = toNumber(pricePerSquareMeter, 0)
        subtotal = unitValue * unitPrice * qty
    } else if (safeMode === 'cubicMeter') {
        unitValue = getVolume(dimensions)
        unitPrice = toNumber(pricePerCubicMeter, 0)
        subtotal = unitValue * unitPrice * qty
    } else if (safeMode === 'symbol') {
        unitValue = Math.max(0, toNumber(symbolsCount, 0))
        unitPrice = toNumber(pricePerSymbol, 0)
        subtotal = unitValue * unitPrice * qty
    } else if (safeMode === 'quantity') {
        unitValue = qty
        unitPrice = toNumber(pricePerUnit, 0)
        subtotal = unitValue * unitPrice
    } else {
        unitValue = qty
        unitPrice = toNumber(price, 0)
        subtotal = unitPrice * qty
    }

    const minimum = toNumber(minPrice, 0)
    const total = minimum > 0 ? Math.max(subtotal, minimum) : subtotal

    return {
        calcMode: safeMode,
        unitValue: roundMoney(unitValue),
        unitPrice: roundMoney(unitPrice),
        subtotal: roundMoney(subtotal),
        minPrice: roundMoney(minimum),
        total: roundMoney(total),
    }
}

const normalizeCalcMode = (value) => {
    const allowed = ['areaThicknessForward', 'volumeForward', 'fixed', 'linearMeter', 'squareMeter', 'cubicMeter', 'mixed']

    return allowed.includes(value) ? value : 'fixed'
}

const normalizeRoundTo = (value) => {
    const number = toNumber(value, 1)

    if (number <= 0) return 1

    return number
}

const normalizePositive = (value, fallback = 0) => {
    const number = toNumber(value, fallback)

    return number > 0 ? number : fallback
}

const normalizeNullablePositive = (value) => {
    const number = toNumber(value, 0)

    return number > 0 ? number : 0
}

export const normalizeMillingDimensions = (dimensions = {}) => {
    const source = asObject(dimensions)

    const height = normalizeNullablePositive(
        source.height ??
        source.h ??
        source.H ??
        source.z ??
        source.Z
    )

    const width = normalizeNullablePositive(
        source.width ??
        source.w ??
        source.W ??
        source.x ??
        source.X
    )

    const thickness = normalizeNullablePositive(
        source.thickness ??
        source.depth ??
        source.length ??
        source.t ??
        source.y ??
        source.Y
    )

    return {
        height,
        width,
        thickness,
    }
}

export const getMillingDimensionsFromXYZ = ({ x, y, z, mmX, mmY, mmZ } = {}) => {
    const values = [mmX ?? x, mmY ?? y, mmZ ?? z]
        .map((value) => normalizeNullablePositive(value))
        .filter((value) => value > 0)

    if (values.length >= 3) {
        const sorted = values.slice(0, 3).sort((a, b) => b - a)

        return {
            height: sorted[0],
            width: sorted[1],
            thickness: sorted[2],
        }
    }

    return normalizeMillingDimensions({
        width: mmX ?? x,
        thickness: mmY ?? y,
        height: mmZ ?? z,
    })
}

export const getDefaultMillingDimensionsForProduct = (product = {}) => {
    const source = asObject(product)
    const pricing = asObject(source.millingPricing)
    const fromPricing = normalizeMillingDimensions(pricing.baseDimensions)

    if (fromPricing.height || fromPricing.width || fromPricing.thickness) {
        return fromPricing
    }

    return normalizeMillingDimensions({
        height: source.height,
        width: source.width,
        thickness: source.thickness ?? source.depth ?? source.length,
    })
}

export const formatMillingDimensions = (dimensions = {}) => {
    const normalized = normalizeMillingDimensions(dimensions)

    const height = normalized.height ? Math.round(normalized.height) : 0
    const width = normalized.width ? Math.round(normalized.width) : 0
    const thickness = normalized.thickness ? Math.round(normalized.thickness) : 0

    if (height && width && thickness) {
        return `${height}×${width}×${thickness}`
    }

    if (height && width) {
        return `${height}×${width}`
    }

    return ''
}

const normalizeMillingRule = (rule = {}, index = 0) => {
    const source = asObject(rule)
    const dimensions = normalizeMillingDimensions(source)

    return {
        id: source.id || source._id || `rule-${index}`,
        label: source.label || source.name || '',
        isBase: source.isBase === true,
        enabled: source.enabled !== false,
        height: dimensions.height,
        width: dimensions.width,
        thickness: dimensions.thickness,
        price: normalizeNullablePositive(source.price ?? source.workPrice ?? source.value),
    }
}

const getMillingRules = (source = {}) => {
    const safeSource = asObject(source)
    const rules =
        safeSource.sizeRules ||
        safeSource.rules ||
        safeSource.prices ||
        safeSource.sizes ||
        safeSource.savedSizes ||
        []

    return Array.isArray(rules)
        ? rules
            .map((rule, index) => normalizeMillingRule(rule, index))
            .filter((rule) => rule.enabled !== false && rule.price > 0 && (rule.height || rule.width || rule.thickness))
        : []
}

const hasAnyMillingPrice = (pricing = {}, product = {}) => {
    const source = asObject(pricing)
    const item = asObject(product)

    if (getMillingRules(source).length > 0) return true

    return (
        normalizeNullablePositive(source.basePrice ?? source.price ?? source.workPrice) > 0 ||
        normalizeNullablePositive(source.pricePerMeter ?? source.workPricePerMeter) > 0 ||
        normalizeNullablePositive(source.pricePerSquareMeter ?? source.workPricePerSquareMeter) > 0 ||
        normalizeNullablePositive(source.pricePerCubicMeter ?? source.workPricePerCubicMeter) > 0 ||
        normalizeNullablePositive(item.workPrice) > 0 ||
        normalizeNullablePositive(item.workPricePerMeter) > 0 ||
        normalizeNullablePositive(item.workPricePerSquareMeter) > 0 ||
        normalizeNullablePositive(item.workPricePerCubicMeter) > 0
    )
}

export const getEnabledWorkOptions = (product = {}) => {
    const source = asObject(product)

    return Array.isArray(source.workOptions)
        ? source.workOptions.filter((option) => option && option.enabled !== false)
        : []
}

export const getPrimaryMillingWorkOption = (product = {}) => {
    return getEnabledWorkOptions(product).find((option) => {
        const type = option?.workType || option?.type || option?.key || ''

        return type === 'milling' || option?.key === 'milling' || /фрез/i.test(String(option?.name || ''))
    }) || null
}

export const getProductMaterialPricingVariants = (product = {}) => {
    const source = asObject(product)
    return Array.isArray(source.materialPricingVariants)
        ? source.materialPricingVariants.filter((variant) => variant && variant.enabled !== false && variant.materialKey)
        : []
}

export const getProductDefaultMaterialKey = (product = {}) => {
    const source = asObject(product)
    const variants = getProductMaterialPricingVariants(source)
    return String(
        source.defaultMaterialKey ||
        variants.find((variant) => variant.isDefault)?.materialKey ||
        source.productPricing?.blankMaterialKey ||
        variants[0]?.materialKey ||
        'gabbro-diabase'
    )
}

export const getProductMaterialPricingVariant = (product = {}, materialKey = '') => {
    const variants = getProductMaterialPricingVariants(product)
    if (!variants.length) return null
    const requested = String(materialKey || '').trim().toLowerCase()
    return variants.find((variant) => String(variant.materialKey || '').toLowerCase() === requested)
        || variants.find((variant) => variant.isDefault)
        || variants[0]
}

export const getPrimaryMillingPricingSource = (product = {}, materialKey = '') => {
    const source = asObject(product)
    const millingWorkOption = getPrimaryMillingWorkOption(source)
    const workPricing = asObject(millingWorkOption?.pricing)
    const rootPricing = asObject(source.millingPricing)
    const variants = getProductMaterialPricingVariants(source)
    const canonicalVariant = variants.find((variant) => (
        normalizeMaterialKey(variant?.materialKey) === 'gabbro-diabase'
    )) || variants.find((variant) => variant?.isDefault === true) || null
    const canonicalPricing = asObject(canonicalVariant?.millingPricing)

    const hasConfigured = (pricing = {}) => (
        pricing?.enabled === true
        || normalizeNullablePositive(pricing?.basePrice) > 0
        || normalizeNullablePositive(pricing?.manualPrice) > 0
        || (Array.isArray(pricing?.sizeRules) && pricing.sizeRules.some((rule) => (
            rule?.enabled !== false && normalizeNullablePositive(rule?.price) > 0
        )))
    )

    // Фрезеровка должна браться только из milling-контура. Раньше здесь
    // использовалась первая включённая работа вообще, поэтому custom/installation
    // могли случайно подменить pricing фрезеровки.
    if (hasConfigured(workPricing)) return workPricing
    if (hasConfigured(rootPricing)) return rootPricing
    if (hasConfigured(canonicalPricing)) return canonicalPricing

    // materialKey оставлен в сигнатуре для обратной совместимости вызовов.
    void materialKey
    return workPricing && Object.keys(workPricing).length
        ? workPricing
        : (rootPricing && Object.keys(rootPricing).length ? rootPricing : canonicalPricing)
}


export const getProductCustomerBasePrice = (product = {}, materialKey = '', profileKey = 'public') => {
    const source = asObject(product)
    const variant = getProductMaterialPricingVariant(source, materialKey)
    const pricing = asObject(variant?.productPricing || source.productPricing)
    const subscriber = normalizeNullablePositive(
        pricing.subscriberPriceCalculated ?? pricing.subscriberPrice
    )
    const publicPrice = normalizeNullablePositive(
        pricing.publicPriceCalculated ?? pricing.publicPrice
    )

    if (profileKey === 'subscriber' && subscriber > 0) return subscriber
    if (profileKey === 'public' && publicPrice > 0) return publicPrice

    return normalizeNullablePositive(
        publicPrice ||
        subscriber ||
        pricing.calculatedPrice ||
        pricing.basePrice ||
        pricing.manualPrice ||
        source.price ||
        source.bayerPrice
    )
}

export const getProductCustomerPricePreviewText = (product = {}, emptyText = 'Цена не указана', materialKey = '') => {
    const price = getProductCustomerBasePrice(product, materialKey)

    if (price <= 0) return emptyText

    return formatMoney(price, emptyText)
}

export const normalizeMillingPricing = (pricingSource = {}, product = {}) => {
    const source = asObject(pricingSource)
    const item = asObject(product)

    const rules = getMillingRules(source)
    const fallbackMode = item.workCalcMode || 'fixed'
    const calcMode = normalizeCalcMode(source.mode || source.calcMode || fallbackMode)
    const baseDimensions = normalizeMillingDimensions(
        source.baseDimensions || getDefaultMillingDimensionsForProduct(item)
    )
    const millingWorkOption = getPrimaryMillingWorkOption(item)
    const baseRule = rules.find((rule) => (
        rule.isBase === true
        || (
            Math.abs(rule.height - baseDimensions.height) <= 0.5
            && Math.abs(rule.width - baseDimensions.width) <= 0.5
            && Math.abs(rule.thickness - baseDimensions.thickness) <= 0.5
        )
    )) || null
    const basePrice = (
        normalizeNullablePositive(source.basePrice)
        || normalizeNullablePositive(baseRule?.price)
        || normalizeNullablePositive(millingWorkOption?.pricing?.basePrice)
        || normalizeNullablePositive(millingWorkOption?.price)
        || (item.workType === 'milling' ? normalizeNullablePositive(item.workPrice) : 0)
    )

    const enabled =
        source.enabled === true ||
        source.isEnabled === true ||
        (source.enabled !== false && source.isEnabled !== false && (
            hasAnyMillingPrice(source, item)
            || basePrice > 0
        ))

    return {
        enabled,
        calcMode,
        basePrice,
        pricePerMeter: normalizeNullablePositive(source.pricePerMeter ?? source.workPricePerMeter ?? item.workPricePerMeter),
        pricePerSquareMeter: normalizeNullablePositive(source.pricePerSquareMeter ?? source.workPricePerSquareMeter ?? item.workPricePerSquareMeter),
        pricePerCubicMeter: normalizeNullablePositive(source.pricePerCubicMeter ?? source.workPricePerCubicMeter ?? item.workPricePerCubicMeter),
        minPrice: normalizeNullablePositive(source.minPrice),
        setupPercent: Math.max(0, toNumber(source.setupPercent, 0)),
        complexityMultiplier: Math.max(0.01, toNumber(source.complexityMultiplier, 1)),
        roundTo: normalizeRoundTo(source.roundTo ?? 1),
        baseDimensions,
        sizeRules: rules,
        axisPreset: source.axisPreset || 'auto',
        useGlobalThicknessPricing: source.useGlobalThicknessPricing !== false,
        thicknessStepMm: Math.max(1, toNumber(source.thicknessStepMm, 10)),
        thicknessMarkupPercent: Math.max(0, toNumber(source.thicknessMarkupPercent, 5)),
        thicknessMarkupMaxPercent: Math.max(0, toNumber(source.thicknessMarkupMaxPercent, 50)),
    }
}


const sameMillingSize = (rule, dimensions, tolerance = 0.5) => {
    const hasHeight = rule.height > 0
    const hasWidth = rule.width > 0
    const hasThickness = rule.thickness > 0

    if (hasHeight && Math.abs(rule.height - dimensions.height) > tolerance) return false
    if (hasWidth && Math.abs(rule.width - dimensions.width) > tolerance) return false
    if (hasThickness && Math.abs(rule.thickness - dimensions.thickness) > tolerance) return false

    return hasHeight || hasWidth || hasThickness
}

const findMillingRule = (rules = [], dimensions = {}) => {
    return rules.find((rule) => sameMillingSize(rule, dimensions)) || null
}

const getMillingAreaSquareMeters = (dimensions = {}) => {
    const height = normalizeNullablePositive(dimensions.height)
    const width = normalizeNullablePositive(dimensions.width)

    return height > 0 && width > 0 ? (height * width) / 1000000 : 0
}

const getMillingVolumeCubicMeters = (dimensions = {}) => {
    const height = normalizeNullablePositive(dimensions.height)
    const width = normalizeNullablePositive(dimensions.width)
    const thickness = normalizeNullablePositive(dimensions.thickness)

    return height > 0 && width > 0 && thickness > 0 ? (height * width * thickness) / 1000000000 : 0
}

const getMillingPerimeterMeters = (dimensions = {}) => {
    const height = normalizeNullablePositive(dimensions.height)
    const width = normalizeNullablePositive(dimensions.width)

    return height > 0 && width > 0 ? ((height + width) * 2) / 1000 : 0
}

const getLegacyMillingPrice = (product = {}) => {
    const item = asObject(product)
    const calcMode = normalizeCalcMode(item.workCalcMode || 'fixed')

    if (calcMode === 'linearMeter') return normalizeNullablePositive(item.workPricePerMeter)
    if (calcMode === 'squareMeter') return normalizeNullablePositive(item.workPricePerSquareMeter)
    if (calcMode === 'cubicMeter') return normalizeNullablePositive(item.workPricePerCubicMeter)

    return normalizeNullablePositive(item.workPrice)
}

const applyMillingPostProcessing = (price, pricing) => {
    let total = normalizeNullablePositive(price)

    if (total <= 0) return 0

    total *= pricing.complexityMultiplier

    if (pricing.setupPercent > 0) {
        total += total * (pricing.setupPercent / 100)
    }

    if (pricing.minPrice > 0) {
        total = Math.max(total, pricing.minPrice)
    }

    return roundByStep(total, pricing.roundTo)
}

export const calcMillingPrice = (pricingSource = {}, dimensions = {}, product = {}) => {
    const pricing = normalizeMillingPricing(pricingSource, product)
    const normalizedDimensions = normalizeMillingDimensions(dimensions)
    const effectiveDimensions = {
        height: normalizedDimensions.height || pricing.baseDimensions.height,
        width: normalizedDimensions.width || pricing.baseDimensions.width,
        thickness: normalizedDimensions.thickness || pricing.baseDimensions.thickness,
    }

    const emptyResult = {
        enabled: pricing.enabled,
        hasPrice: false,
        total: 0,
        subtotal: 0,
        unitValue: 0,
        unitPrice: 0,
        source: 'none',
        sourceLabel: 'Цена не указана',
        calcMode: pricing.calcMode,
        dimensions: effectiveDimensions,
        dimensionsLabel: formatMillingDimensions(effectiveDimensions),
        pricing,
    }

    const matchedRule = findMillingRule(pricing.sizeRules, effectiveDimensions)

    // ADMIN сначала проверяет точную строку размера, затем уже формулу.
    // Повторяем тот же контракт на FRONT.
    if (matchedRule) {
        return {
            ...emptyResult,
            hasPrice: true,
            total: matchedRule.price,
            subtotal: matchedRule.price,
            unitPrice: matchedRule.price,
            source: 'rule',
            sourceLabel: matchedRule.label || `Цена из списка: ${formatMillingDimensions(matchedRule)}`,
            matchedRule,
        }
    }

    if (!pricing.enabled) {
        const legacyPrice = pricing.basePrice || getLegacyMillingPrice(product)

        if (legacyPrice <= 0) return emptyResult

        return {
            ...emptyResult,
            enabled: false,
            hasPrice: true,
            total: legacyPrice,
            subtotal: legacyPrice,
            unitPrice: legacyPrice,
            source: 'legacy',
            sourceLabel: 'Основная цена фрезеровки',
        }
    }

    if (pricing.calcMode === 'areaThicknessForward') {
        const baseHeight = normalizeNullablePositive(pricing.baseDimensions.height)
        const baseWidth = normalizeNullablePositive(pricing.baseDimensions.width)
        const baseThickness = normalizeNullablePositive(pricing.baseDimensions.thickness)
        const currentHeight = normalizeNullablePositive(effectiveDimensions.height)
        const currentWidth = normalizeNullablePositive(effectiveDimensions.width)
        const currentThickness = normalizeNullablePositive(effectiveDimensions.thickness)
        const baseArea = baseHeight * baseWidth
        const currentArea = currentHeight * currentWidth
        const basePrice = normalizeNullablePositive(pricing.basePrice)

        if (!(basePrice > 0) || !(baseArea > 0) || !(currentArea > 0)) return emptyResult

        const areaRatio = Math.max(1, currentArea / baseArea)
        const publicPolicy = asObject(publicBlankPricingSnapshot.pricingPolicy)
        const useGlobal = pricing.useGlobalThicknessPricing !== false
        const thicknessStepMm = Math.max(1, toNumber(
            useGlobal ? publicPolicy.millingThicknessStepMm : pricing.thicknessStepMm,
            pricing.thicknessStepMm || 10
        ))
        const thicknessMarkupPerStep = Math.max(0, toNumber(
            useGlobal ? publicPolicy.millingThicknessMarkupPercent : pricing.thicknessMarkupPercent,
            pricing.thicknessMarkupPercent || 5
        ))
        const thicknessMarkupMaxPercent = Math.max(0, toNumber(
            useGlobal ? publicPolicy.millingThicknessMarkupMaxPercent : pricing.thicknessMarkupMaxPercent,
            pricing.thicknessMarkupMaxPercent || 50
        ))
        const extraThickness = Math.max(0, currentThickness - baseThickness)
        const thicknessSteps = extraThickness > 0
            ? Math.ceil(extraThickness / thicknessStepMm)
            : 0
        const thicknessMarkupPercent = Math.min(
            thicknessMarkupMaxPercent,
            thicknessSteps * thicknessMarkupPerStep
        )
        const rawPrice = basePrice * areaRatio * (1 + thicknessMarkupPercent / 100)
        const total = roundByStep(rawPrice, pricing.roundTo)

        return {
            ...emptyResult,
            hasPrice: total > 0,
            total,
            subtotal: roundMoney(rawPrice),
            unitValue: roundMoney(areaRatio),
            unitPrice: basePrice,
            source: 'area-thickness-formula',
            sourceLabel: 'Фрезеровка по площади и толщине',
            areaRatio,
            thicknessSteps,
            thicknessStepMm,
            thicknessMarkupPerStep,
            thicknessMarkupPercent,
            thicknessMarkupMaxPercent,
            rawPrice,
        }
    }

    let unitValue = 1
    let unitPrice = pricing.basePrice
    let subtotal = pricing.basePrice
    let total = 0
    let sourceLabel = 'Формула'

    if (pricing.calcMode === 'volumeForward') {
        const baseVolume = getMillingVolumeCubicMeters(pricing.baseDimensions)
        const currentVolume = getMillingVolumeCubicMeters(effectiveDimensions)
        const rawRatio = baseVolume > 0 && currentVolume > 0 ? currentVolume / baseVolume : 1
        const volumeRatio = Math.max(1, rawRatio)
        const isIncreased = rawRatio > 1.000001

        unitValue = volumeRatio
        unitPrice = pricing.basePrice
        subtotal = pricing.basePrice * volumeRatio
        total = subtotal * pricing.complexityMultiplier

        if (isIncreased && pricing.setupPercent > 0) {
            total += total * (pricing.setupPercent / 100)
        }

        if (pricing.minPrice > 0) {
            total = Math.max(total, pricing.minPrice)
        }

        total = roundByStep(total, pricing.roundTo)
        sourceLabel = 'По объёму: только вверх'
    } else {
        if (pricing.calcMode === 'linearMeter') {
            unitValue = getMillingPerimeterMeters(effectiveDimensions)
            unitPrice = pricing.pricePerMeter
            subtotal = unitValue * unitPrice
        } else if (pricing.calcMode === 'squareMeter') {
            unitValue = getMillingAreaSquareMeters(effectiveDimensions)
            unitPrice = pricing.pricePerSquareMeter
            subtotal = unitValue * unitPrice
        } else if (pricing.calcMode === 'cubicMeter') {
            unitValue = getMillingVolumeCubicMeters(effectiveDimensions)
            unitPrice = pricing.pricePerCubicMeter
            subtotal = unitValue * unitPrice
        } else if (pricing.calcMode === 'mixed') {
            const baseArea = getMillingAreaSquareMeters(pricing.baseDimensions)
            const currentArea = getMillingAreaSquareMeters(effectiveDimensions)
            const areaRatio = baseArea > 0 && currentArea > 0 ? currentArea / baseArea : 1

            const baseThickness = normalizePositive(pricing.baseDimensions.thickness, effectiveDimensions.thickness || 1)
            const thicknessRatio = effectiveDimensions.thickness > 0 ? effectiveDimensions.thickness / baseThickness : 1
            const thicknessExtra = thicknessRatio > 1 ? 1 + ((thicknessRatio - 1) * 0.35) : 1

            unitValue = areaRatio * thicknessExtra
            unitPrice = pricing.basePrice
            subtotal = pricing.basePrice * unitValue
        }

        total = applyMillingPostProcessing(subtotal, pricing)

        const sourceLabelByMode = {
            fixed: 'Фиксированная цена',
            linearMeter: 'Формула по периметру',
            squareMeter: 'Формула по площади',
            cubicMeter: 'Формула по объёму',
            mixed: 'Смешанная формула',
        }

        sourceLabel = sourceLabelByMode[pricing.calcMode] || 'Формула'
    }

    if (total <= 0) return emptyResult

    return {
        ...emptyResult,
        hasPrice: true,
        total,
        subtotal: roundMoney(subtotal),
        unitValue: roundMoney(unitValue),
        unitPrice: roundMoney(unitPrice),
        source: 'formula',
        sourceLabel,
    }
}

export const getMillingSizeRules = (product = {}, materialKey = '') => {
    const pricing = normalizeMillingPricing(getPrimaryMillingPricingSource(product, materialKey), product)

    return pricing.sizeRules
}

export const getMillingPricePreview = (product = {}, materialKey = '') => {
    const pricingSource = getPrimaryMillingPricingSource(product, materialKey)
    const pricing = normalizeMillingPricing(pricingSource, product)
    const dimensions = pricing.baseDimensions || getDefaultMillingDimensionsForProduct(product)
    const result = calcMillingPrice(pricingSource, dimensions, product)

    return result
}

export const getMillingPricePreviewText = (product = {}, emptyText = 'Цена не указана', materialKey = '') => {
    const result = getMillingPricePreview(product, materialKey)

    if (!result.hasPrice) return emptyText

    const prefix = result.source === 'rule' || result.source === 'formula'
        ? 'от '
        : ''

    return `${prefix}${formatMoney(result.total, emptyText)}`
}


const sameDimensionsMm = (left = {}, right = {}, tolerance = 0.5) => (
    ['height', 'width', 'thickness'].every((key) => {
        const leftValue = normalizeNullablePositive(left?.[key])
        const rightValue = normalizeNullablePositive(right?.[key])
        if (!(leftValue > 0) || !(rightValue > 0)) return false
        return Math.abs(leftValue - rightValue) <= tolerance
    })
)

const normalizeBlankCategory = (blankType = 'stele', thickness = 0, value = '') => {
    const type = normalizeMaterialKey(blankType || 'stele') || 'stele'
    if (type !== 'slab') return ''
    const explicit = normalizeMaterialKey(value || '')
    if (explicit === 'tile' || explicit === 'ledger') return explicit
    return Math.round(normalizeNullablePositive(thickness)) <= 20 ? 'tile' : 'ledger'
}

const ceilDimensionStep = (value, step, minimum = 0) => {
    const safeValue = Math.max(normalizeNullablePositive(value), Math.max(0, toNumber(minimum, 0)))
    const safeStep = Math.max(1, toNumber(step, 1))
    if (!(safeValue > 0)) return 0
    return Math.ceil(safeValue / safeStep) * safeStep
}

export const resolveProductBlankDimensions = (dimensions = {}, productPricing = {}) => {
    const current = normalizeMillingDimensions(dimensions)
    const pricing = asObject(productPricing)
    const mode = ['standard', 'exact', 'manual'].includes(pricing.blankSizingMode)
        ? pricing.blankSizingMode
        : 'standard'

    if (mode === 'manual') {
        const manual = normalizeMillingDimensions(pricing.manualBlankDimensions)
        return {
            height: manual.height || current.height,
            width: manual.width || current.width,
            thickness: manual.thickness || current.thickness,
            mode,
            source: 'manual',
        }
    }

    if (mode === 'exact') {
        return { ...current, mode, source: 'exact' }
    }

    const policy = asObject(publicBlankPricingSnapshot.pricingPolicy)
    const heightStep = Math.max(1, toNumber(policy.blankHeightStepMm, toNumber(pricing.blankHeightStepMm, 50)))
    const widthStep = Math.max(1, toNumber(policy.blankWidthStepMm, toNumber(pricing.blankWidthStepMm, 50)))
    const thicknessStep = Math.max(1, toNumber(policy.blankThicknessStepMm, toNumber(pricing.blankThicknessStepMm, 10)))
    const minimumThickness = Math.max(1, toNumber(policy.blankMinimumThicknessMm, toNumber(pricing.blankMinimumThicknessMm, 30)))

    return {
        height: ceilDimensionStep(current.height, heightStep),
        width: ceilDimensionStep(current.width, widthStep),
        thickness: ceilDimensionStep(current.thickness, thicknessStep, minimumThickness),
        mode,
        source: 'standard-rounded',
        steps: { height: heightStep, width: widthStep, thickness: thicknessStep },
        minimumThickness,
    }
}

const getRuleProfilePrice = (rule = {}, profileKey = 'public') => {
    const subscriberPrice = normalizeNullablePositive(rule.subscriberPrice)
    const publicPrice = normalizeNullablePositive(rule.publicPrice)
    return profileKey === 'subscriber'
        ? (subscriberPrice || publicPrice)
        : (publicPrice || subscriberPrice)
}

const publicRuleMatchesKind = (rule = {}, options = {}) => (
    normalizeMaterialKey(rule.blankType || 'stele') === normalizeMaterialKey(options.blankType || 'stele')
    && (!options.blankCategory || normalizeBlankCategory(rule.blankType, rule.thickness, rule.blankCategory) === options.blankCategory)
    && normalizeMaterialKey(rule.polishingType || 'two-side') === normalizeMaterialKey(options.polishingType || 'two-side')
)

const publicRuleMetric = (dimensions = {}, metric = 'volume') => {
    const normalized = normalizeMillingDimensions(dimensions)
    if (metric === 'length') return normalized.height / 1000
    if (metric === 'area') return (normalized.height * normalized.width) / 1000000
    return (normalized.height * normalized.width * normalized.thickness) / 1000000000
}

const getPublicEstimationStrategy = (rules = [], dimensions = {}, blankType = 'stele') => {
    const type = normalizeMaterialKey(blankType || 'stele') || 'stele'
    const targetWidth = Math.round(normalizeNullablePositive(dimensions.width))
    const targetThickness = Math.round(normalizeNullablePositive(dimensions.thickness))

    if (type === 'base' || type === 'flower-bed') {
        const sameCrossSection = rules.filter((rule) => (
            Math.round(normalizeNullablePositive(rule.width)) === targetWidth
            && Math.round(normalizeNullablePositive(rule.thickness)) === targetThickness
        ))
        if (sameCrossSection.length >= 2) return { rules: sameCrossSection, metric: 'length' }
    }

    if (type === 'stele') {
        const sameThickness = rules.filter((rule) => (
            Math.round(normalizeNullablePositive(rule.thickness)) === targetThickness
        ))
        if (sameThickness.length >= 2) return { rules: sameThickness, metric: 'area' }
    }

    if (type === 'slab' || type === 'bench' || type === 'table') {
        const sameThickness = rules.filter((rule) => (
            Math.round(normalizeNullablePositive(rule.thickness)) === targetThickness
        ))
        return { rules: sameThickness.length >= 2 ? sameThickness : rules, metric: 'area' }
    }

    return { rules, metric: type === 'support' ? 'volume' : 'volume' }
}

const calculatePublicRuleCustomerPrice = ({ rule, dimensions, profileKey }) => {
    const storedPrice = getRuleProfilePrice(rule, profileKey)
    if (!(storedPrice > 0)) return 0

    const priceUnit = String(rule.priceUnit || 'piece')
    if (priceUnit === 'squareMeter') {
        const ruleArea = publicRuleMetric(rule, 'area')
        const targetArea = publicRuleMetric(dimensions, 'area')
        if (ruleArea > 0 && targetArea > 0) return roundMoney(storedPrice * targetArea / ruleArea)
    }

    if (priceUnit === 'cubicMeter') {
        const ruleVolume = publicRuleMetric(rule, 'volume')
        const targetVolume = publicRuleMetric(dimensions, 'volume')
        if (ruleVolume > 0 && targetVolume > 0) return roundMoney(storedPrice * targetVolume / ruleVolume)
    }

    return storedPrice
}

const findExactPublicBlankRule = ({ catalog, dimensions, blankType, blankCategory, polishingType, profileKey }) => {
    const rules = Array.isArray(catalog?.sizeRules) ? catalog.sizeRules : []
    const areaM2 = publicRuleMetric(dimensions, 'area')
    const options = { blankType, blankCategory, polishingType }

    return rules.find((rule) => {
        if (!publicRuleMatchesKind(rule, options) || !(getRuleProfilePrice(rule, profileKey) > 0)) return false

        if (String(rule.priceUnit || 'piece') === 'squareMeter') {
            const sameThickness = Math.round(normalizeNullablePositive(rule.thickness)) === Math.round(normalizeNullablePositive(dimensions.thickness))
            const minArea = Math.max(0, toNumber(rule.minAreaM2, 0))
            const maxArea = Math.max(0, toNumber(rule.maxAreaM2, 0))
            return sameThickness && (!minArea || areaM2 >= minArea) && (!maxArea || areaM2 <= maxArea)
        }

        return sameDimensionsMm(rule, dimensions)
    }) || null
}

const estimatePublicBlankCustomerPrice = ({ catalog, dimensions, blankType, blankCategory, polishingType, profileKey }) => {
    const allRules = (Array.isArray(catalog?.sizeRules) ? catalog.sizeRules : [])
        .filter((rule) => (
            String(rule?.priceUnit || 'piece') === 'piece'
            && publicRuleMatchesKind(rule, { blankType, blankCategory, polishingType })
            && getRuleProfilePrice(rule, profileKey) > 0
        ))

    const strategy = getPublicEstimationStrategy(allRules, dimensions, blankType)
    const targetMetric = publicRuleMetric(dimensions, strategy.metric)
    const prepared = strategy.rules.map((rule) => {
        const price = getRuleProfilePrice(rule, profileKey)
        const metric = publicRuleMetric(rule, strategy.metric)
        const aspect = normalizeNullablePositive(rule.width) > 0
            ? normalizeNullablePositive(rule.height) / normalizeNullablePositive(rule.width)
            : 1
        const targetAspect = normalizeNullablePositive(dimensions.width) > 0
            ? normalizeNullablePositive(dimensions.height) / normalizeNullablePositive(dimensions.width)
            : 1
        const aspectPenalty = strategy.metric === 'area' || strategy.metric === 'volume'
            ? Math.abs(Math.log(Math.max(0.01, aspect / targetAspect)))
            : 0

        return {
            rule,
            price,
            metric,
            aspectPenalty,
            distance: Math.abs(Math.log(Math.max(0.000001, metric / targetMetric))),
        }
    }).filter((item) => item.price > 0 && item.metric > 0)

    if (prepared.length < 2 || !(targetMetric > 0)) return null

    prepared.sort((left, right) => (
        (left.distance + left.aspectPenalty * 0.35)
        - (right.distance + right.aspectPenalty * 0.35)
    ))
    const nearest = prepared.slice(0, Math.min(5, prepared.length))

    let weightedRate = 0
    let weightTotal = 0
    nearest.forEach((item) => {
        const weight = 1 / Math.max(0.08, item.distance + item.aspectPenalty * 0.35)
        weightedRate += (item.price / item.metric) * weight
        weightTotal += weight
    })

    const minMetric = Math.min(...prepared.map((item) => item.metric))
    const maxMetric = Math.max(...prepared.map((item) => item.metric))
    const outsideRatio = targetMetric > maxMetric
        ? targetMetric / maxMetric
        : targetMetric < minMetric
            ? minMetric / targetMetric
            : 1
    const extrapolationPremium = targetMetric > maxMetric
        ? 1 + Math.min(0.15, Math.log(outsideRatio) * 0.1)
        : 1

    let baseRate = weightedRate / weightTotal
    let shapePremium = 1
    if (targetMetric > maxMetric) {
        const upperBoundary = [...prepared].sort((left, right) => right.metric - left.metric).slice(0, Math.min(3, prepared.length))
        baseRate = Math.max(...upperBoundary.map((item) => item.price / item.metric))

        if (strategy.metric === 'area' || strategy.metric === 'volume') {
            const boundaryRule = upperBoundary[0]?.rule || {}
            const boundaryAspect = normalizeNullablePositive(boundaryRule.width) > 0
                ? normalizeNullablePositive(boundaryRule.height) / normalizeNullablePositive(boundaryRule.width)
                : 1
            const targetAspect = normalizeNullablePositive(dimensions.width) > 0
                ? normalizeNullablePositive(dimensions.height) / normalizeNullablePositive(dimensions.width)
                : 1
            shapePremium = 1 + Math.min(0.1, Math.abs(Math.log(Math.max(0.01, targetAspect / boundaryAspect))) * 0.12)
        }
    }

    const price = roundMoneyByStep(
        baseRate * targetMetric * extrapolationPremium * shapePremium,
        publicBlankPricingSnapshot.pricingPolicy?.roundTo || 0.01
    )

    return price > 0 ? {
        price,
        source: targetMetric >= minMetric && targetMetric <= maxMetric ? 'public-interpolation' : 'public-extrapolation',
        estimated: true,
    } : null
}

const calculatePublicBlankCustomerPrice = ({
    catalog,
    dimensions,
    blankType = 'stele',
    blankCategory = '',
    polishingType = 'two-side',
    profileKey = 'public',
    allowEstimated = true,
}) => {
    if (!catalog) return { price: 0, source: 'missing-material', estimated: false }

    const exactRule = findExactPublicBlankRule({
        catalog,
        dimensions,
        blankType,
        blankCategory,
        polishingType,
        profileKey,
    })

    if (exactRule) {
        return {
            price: calculatePublicRuleCustomerPrice({ rule: exactRule, dimensions, profileKey }),
            source: 'exact-public-rule',
            estimated: exactRule.estimated === true,
            rule: exactRule,
        }
    }

    if (allowEstimated !== false) {
        const estimate = estimatePublicBlankCustomerPrice({
            catalog,
            dimensions,
            blankType,
            blankCategory,
            polishingType,
            profileKey,
        })
        if (estimate?.price > 0) return estimate
    }

    return { price: 0, source: 'missing-public-price', estimated: false }
}

export const normalizeWorkDimensions = (dimensions = {}) => {
    const unit = String(dimensions?.unit || 'mm').toLowerCase()
    const factor = unit === 'mm' ? 1 / 1000 : 1
    const height = Math.max(0, toNumber(dimensions?.height, 0) * factor)
    const width = Math.max(0, toNumber(dimensions?.width, 0) * factor)
    const thickness = Math.max(0, toNumber(dimensions?.thickness ?? dimensions?.length, 0) * factor)

    return {
        unit: 'm',
        height,
        width,
        length: thickness,
        thickness,
        linearMeters: Math.max(height, width),
        area: height * width,
        volume: height * width * thickness,
    }
}

const findExactWorkSizeRule = (pricing = {}, dimensions = {}) => (
    (Array.isArray(pricing?.sizeRules) ? pricing.sizeRules : []).find((rule) => (
        rule?.enabled !== false
        && toNumber(rule?.price, 0) > 0
        && sameDimensionsMm(rule, dimensions)
    )) || null
)

export const calculateWorkOptionPrice = ({ workOption = {}, dimensions = {}, quantity = 1 } = {}) => {
    if (!workOption || workOption.enabled === false) {
        return { total: 0, source: 'disabled', rule: null }
    }

    const exactRule = findExactWorkSizeRule(workOption.pricing || {}, dimensions)
    if (exactRule) {
        return {
            total: roundMoney(toNumber(exactRule.price, 0) * Math.max(1, toNumber(quantity, 1))),
            source: 'size-rule',
            rule: exactRule,
        }
    }

    const calculation = calculateByMode({
        calcMode: workOption.calcMode || 'fixed',
        price: workOption.price,
        pricePerMeter: workOption.pricePerMeter,
        pricePerSquareMeter: workOption.pricePerSquareMeter,
        pricePerCubicMeter: workOption.pricePerCubicMeter,
        dimensions: normalizeWorkDimensions(dimensions),
        quantity,
    })

    return {
        total: calculation.total,
        source: calculation.calcMode,
        rule: null,
        calculation,
    }
}

export const calculateWorkOptionsSummary = ({ workOptions = [], dimensions = {}, quantity = 1, excludeMilling = false } = {}) => {
    const items = (Array.isArray(workOptions) ? workOptions : [])
        .filter((work) => work?.enabled !== false)
        .filter((work) => !(excludeMilling && (work?.workType === 'milling' || work?.key === 'milling')))
        .map((work, index) => {
            const calculation = calculateWorkOptionPrice({ workOption: work, dimensions, quantity })
            return {
                key: String(work?.key || `${work?.workType || 'work'}-${index}`),
                name: String(work?.name || work?.workType || 'Работа'),
                workType: String(work?.workType || 'custom'),
                total: roundMoney(calculation.total),
                source: calculation.source,
                rule: calculation.rule || null,
            }
        })

    return {
        items,
        total: roundMoney(items.reduce((sum, item) => sum + toNumber(item.total, 0), 0)),
    }
}

const getStoredCustomerProfilePrice = ({ product, pricing, dimensions, profileKey }) => {
    const rules = Array.isArray(pricing?.sizeRules) ? pricing.sizeRules : []
    const exact = rules.find((rule) => rule?.enabled !== false && sameDimensionsMm(rule, dimensions)) || null
    const field = profileKey === 'subscriber' ? 'subscriberPrice' : 'publicPrice'
    const calculatedField = profileKey === 'subscriber' ? 'subscriberPriceCalculated' : 'publicPriceCalculated'
    const exactPrice = normalizeNullablePositive(exact?.[field])
    const calculatedPrice = normalizeNullablePositive(pricing?.[calculatedField] ?? pricing?.[field])
    const technicalFallback = normalizeNullablePositive(
        pricing?.calculatedPrice
        || pricing?.basePrice
        || pricing?.manualPrice
        || product?.price
        || product?.bayerPrice
    )

    return exactPrice || calculatedPrice || technicalFallback
}

const applyPublicMaterialMillingMarkup = ({ millingResult, materialKey }) => {
    if (!millingResult?.hasPrice) return millingResult
    const catalog = getPublicBlankMaterial(materialKey)
    const markupPercent = Math.max(0, toNumber(catalog?.millingMarkupPercent, 0))
    if (!(markupPercent > 0)) return millingResult

    const rawPrice = toNumber(millingResult.total, 0) * (1 + markupPercent / 100)
    const total = roundMoneyByStep(rawPrice, publicBlankPricingSnapshot.pricingPolicy?.roundTo || 0.01)

    return {
        ...millingResult,
        total,
        subtotal: total,
        source: `${millingResult.source || 'milling'}+material-markup`,
        sourceLabel: `${millingResult.sourceLabel || 'Фрезеровка'} + ${markupPercent}% за материал`,
        materialMarkupPercent: markupPercent,
        materialMarkupAmount: roundMoney(total - toNumber(millingResult.total, 0)),
    }
}

export const calculateProductCustomerPriceForDimensions = ({
    product = {},
    dimensions = {},
    materialKey = '',
    profileKey = 'public',
} = {}) => {
    const source = asObject(product)
    const normalizedDimensions = normalizeMillingDimensions(dimensions)
    const requestedMaterialKey = normalizeMaterialKey(materialKey || getProductDefaultMaterialKey(source))
    const variants = getProductMaterialPricingVariants(source)
    const exactVariant = variants.find((variant) => normalizeMaterialKey(variant?.materialKey) === requestedMaterialKey) || null
    const missingRequestedVariant = !!materialKey && variants.length > 0 && !exactVariant
    const variant = exactVariant || variants.find((item) => item?.isDefault) || variants[0] || null
    const effectiveMaterialKey = normalizeMaterialKey(variant?.materialKey || requestedMaterialKey || getProductDefaultMaterialKey(source))
    const pricing = asObject(variant?.productPricing || source.productPricing)
    const storedSubscriberPrice = missingRequestedVariant ? 0 : getStoredCustomerProfilePrice({
        product: source,
        pricing,
        dimensions: normalizedDimensions,
        profileKey: 'subscriber',
    })
    const storedPublicPrice = missingRequestedVariant ? 0 : getStoredCustomerProfilePrice({
        product: source,
        pricing,
        dimensions: normalizedDimensions,
        profileKey: 'public',
    })

    if (missingRequestedVariant) {
        return {
            ok: false,
            missingRequestedVariant: true,
            materialKey: requestedMaterialKey,
            materialName: '',
            subscriberPrice: 0,
            publicPrice: 0,
            effectivePrice: 0,
            millingPrice: 0,
            worksPrice: 0,
            millingPriceInfo: null,
            blankDimensions: normalizedDimensions,
        }
    }

    const millingSource = getPrimaryMillingPricingSource(source, effectiveMaterialKey)
    const baseMillingResult = calcMillingPrice(millingSource, normalizedDimensions, source)
    const millingResult = applyPublicMaterialMillingMarkup({
        millingResult: baseMillingResult,
        materialKey: effectiveMaterialKey,
    })
    const enabledWorkOptions = getEnabledWorkOptions(source)
    const hasExplicitMillingWork = enabledWorkOptions.some((item) => (
        item?.workType === 'milling' || item?.key === 'milling'
    ))
    const normalizedMillingSource = normalizeMillingPricing(millingSource, source)
    const hasMillingWork = (
        hasExplicitMillingWork
        || normalizedMillingSource.enabled === true
        || variant?.millingPricing?.enabled === true
        || source?.millingPricing?.enabled === true
        || source?.workType === 'milling'
    )
    const additionalWorks = calculateWorkOptionsSummary({
        workOptions: enabledWorkOptions,
        dimensions: normalizedDimensions,
        excludeMilling: true,
    })
    const millingPrice = hasMillingWork && millingResult?.hasPrice
        ? toNumber(millingResult.total, 0)
        : 0
    const worksPrice = roundMoney(millingPrice + additionalWorks.total)

    if (pricing.mode !== 'blankPlusMilling') {
        const effectivePrice = profileKey === 'subscriber'
            ? (storedSubscriberPrice || storedPublicPrice)
            : (storedPublicPrice || storedSubscriberPrice)
        return {
            ok: effectivePrice > 0,
            dynamic: false,
            missingRequestedVariant: false,
            materialKey: effectiveMaterialKey,
            materialName: String(variant?.materialName || pricing.blankMaterialName || ''),
            subscriberPrice: storedSubscriberPrice,
            publicPrice: storedPublicPrice,
            effectivePrice,
            millingPrice,
            worksPrice,
            millingPriceInfo: millingResult?.hasPrice ? millingResult : null,
            blankDimensions: normalizedDimensions,
        }
    }

    const blankDimensions = resolveProductBlankDimensions(normalizedDimensions, pricing)
    const catalog = getPublicBlankMaterial(effectiveMaterialKey)
    const blankType = pricing.blankType || 'stele'
    const blankCategory = normalizeBlankCategory(blankType, blankDimensions.thickness, pricing.blankCategory || '')
    const polishingType = pricing.blankPolishingType || pricing.polishingType || 'two-side'
    const subscriberBlank = calculatePublicBlankCustomerPrice({
        catalog,
        dimensions: blankDimensions,
        blankType,
        blankCategory,
        polishingType,
        profileKey: 'subscriber',
        allowEstimated: pricing.allowEstimatedBlankPrice !== false,
    })
    const publicBlank = calculatePublicBlankCustomerPrice({
        catalog,
        dimensions: blankDimensions,
        blankType,
        blankCategory,
        polishingType,
        profileKey: 'public',
        allowEstimated: pricing.allowEstimatedBlankPrice !== false,
    })

    const baseDimensions = normalizeMillingDimensions(pricing.baseDimensions)
    const baseMillingSource = getPrimaryMillingPricingSource(source, effectiveMaterialKey)
    const baseMilling = applyPublicMaterialMillingMarkup({
        millingResult: calcMillingPrice(baseMillingSource, baseDimensions, source),
        materialKey: effectiveMaterialKey,
    })
    const baseAdditional = calculateWorkOptionsSummary({
        workOptions: enabledWorkOptions,
        dimensions: baseDimensions,
        excludeMilling: true,
    })
    const baseWorksPrice = roundMoney(
        (hasMillingWork && baseMilling?.hasPrice ? toNumber(baseMilling.total, 0) : 0)
        + baseAdditional.total
    )
    const fallbackSubscriberBlank = Math.max(0, storedSubscriberPrice - baseWorksPrice)
    const fallbackPublicBlank = Math.max(0, storedPublicPrice - baseWorksPrice)
    const hasBlankOverride = normalizeNullablePositive(pricing.blankPriceOverride) > 0
    const subscriberBlankPrice = hasBlankOverride
        ? fallbackSubscriberBlank
        : (subscriberBlank.price || fallbackSubscriberBlank)
    const publicBlankPrice = hasBlankOverride
        ? fallbackPublicBlank
        : (publicBlank.price || fallbackPublicBlank)

    const subscriberPrice = roundMoney(subscriberBlankPrice + worksPrice)
    const publicPrice = roundMoney(publicBlankPrice + worksPrice)
    const effectivePrice = profileKey === 'subscriber'
        ? (subscriberPrice || publicPrice)
        : (publicPrice || subscriberPrice)

    return {
        ok: effectivePrice > 0,
        dynamic: true,
        missingRequestedVariant: false,
        materialKey: effectiveMaterialKey,
        materialName: String(catalog?.name || variant?.materialName || pricing.blankMaterialName || ''),
        subscriberPrice,
        publicPrice,
        effectivePrice,
        blankSubscriberPrice: subscriberBlankPrice,
        blankPublicPrice: publicBlankPrice,
        blankDimensions,
        blankSource: hasBlankOverride
            ? 'stored-blank-override'
            : (publicBlank.source || subscriberBlank.source || 'fallback'),
        blankEstimated: publicBlank.estimated === true || subscriberBlank.estimated === true,
        millingPrice,
        worksPrice,
        additionalWorksPrice: additionalWorks.total,
        millingPriceInfo: millingResult?.hasPrice ? millingResult : null,
        pricingMode: pricing.mode,
    }
}

export const calculateProductionOperationPrice = ({
    tariff,
    dimensions,
    quantity,
    symbolsCount,
}) => {
    const result = calculateByMode({
        calcMode: tariff?.calcMode,
        price: tariff?.price,
        pricePerMeter: tariff?.pricePerMeter,
        pricePerSquareMeter: tariff?.pricePerSquareMeter,
        pricePerCubicMeter: tariff?.pricePerCubicMeter,
        pricePerSymbol: tariff?.pricePerSymbol,
        pricePerUnit: tariff?.pricePerUnit,
        minPrice: tariff?.minPrice,
        dimensions,
        quantity,
        symbolsCount,
    })

    const materialResult = calculateByMode({
        calcMode: tariff?.calcMode,
        price: tariff?.materialPrice,
        pricePerMeter: tariff?.materialPricePerMeter,
        pricePerSquareMeter: tariff?.materialPricePerSquareMeter,
        pricePerCubicMeter: tariff?.materialPricePerCubicMeter,
        pricePerSymbol: 0,
        pricePerUnit: 0,
        minPrice: 0,
        dimensions,
        quantity,
        symbolsCount,
    })

    const useMaterial = tariff?.materialPolicy === 'office'

    return {
        operationTotal: result.total,
        materialTotal: useMaterial ? materialResult.total : 0,
        total: roundMoney(result.total + (useMaterial ? materialResult.total : 0)),
        operation: result,
        material: materialResult,
        materialPolicy: tariff?.materialPolicy || 'customer',
    }
}

export const calculateRetailOperationPrice = ({
    priceConfig,
    dimensions,
    quantity,
    symbolsCount,
}) => {
    const result = calculateByMode({
        calcMode: priceConfig?.calcMode,
        price: priceConfig?.retailPrice,
        pricePerMeter: priceConfig?.retailPricePerMeter,
        pricePerSquareMeter: priceConfig?.retailPricePerSquareMeter,
        pricePerCubicMeter: priceConfig?.retailPricePerCubicMeter,
        pricePerSymbol: priceConfig?.retailPricePerSymbol,
        pricePerUnit: priceConfig?.retailPricePerUnit,
        minPrice: priceConfig?.minRetailPrice,
        dimensions,
        quantity,
        symbolsCount,
    })

    const materialResult = calculateByMode({
        calcMode: priceConfig?.calcMode,
        price: priceConfig?.materialRetailPrice,
        pricePerMeter: priceConfig?.materialRetailPricePerMeter,
        pricePerSquareMeter: priceConfig?.materialRetailPricePerSquareMeter,
        pricePerCubicMeter: priceConfig?.materialRetailPricePerCubicMeter,
        pricePerSymbol: 0,
        pricePerUnit: 0,
        minPrice: 0,
        dimensions,
        quantity,
        symbolsCount,
    })

    const useMaterial = priceConfig?.materialPolicy === 'office'

    return {
        operationTotal: result.total,
        materialTotal: useMaterial ? materialResult.total : 0,
        total: roundMoney(result.total + (useMaterial ? materialResult.total : 0)),
        operation: result,
        material: materialResult,
        materialPolicy: priceConfig?.materialPolicy || 'customer',
    }
}
